/**
 * Recording-Safe Page Reload
 *
 * Wraps window.location.reload() with a guard that checks whether
 * an AI Scribe recording session is active. If it is, the reload
 * is suppressed and a persistent toast warns the user to refresh
 * after their recording completes.
 *
 * Use this anywhere a full page reload would otherwise fire
 * (chunk errors, error recovery, etc.).
 */

import { toast } from '@/lib/toast';
import { useRecordingSessionStore } from '@/lib/stores/recording-session-store';

const TOAST_ID = 'stale-app-recording';
const TOAST_MESSAGE =
  'Your app is out of date. Please refresh the page once your recording is completed.';

/**
 * Reload the page — unless a recording is active.
 *
 * @param delay  ms to wait before reloading (default 1000)
 * @param reason optional context for logging (e.g. the error that triggered the reload)
 * @returns true if reload was scheduled, false if suppressed
 */
export function recordingSafeReload(
  delay: number = 1000,
  reason?: unknown
): boolean {
  if (useRecordingSessionStore.getState().isRecording) {
    console.warn('Page reload suppressed — recording is active', reason ?? '');
    toast.warning(TOAST_MESSAGE, { id: TOAST_ID, duration: Infinity });
    return false;
  }

  if (reason) {
    console.warn('Scheduling page reload', reason);
  }
  setTimeout(() => {
    window.location.reload();
  }, delay);
  return true;
}
